/**
 * PdpRail: sequences the two-column product page scroll.
 *
 * Scrolling down: right column (form) scrolls to bottom before left
 *   media-extras (accordions) enter the viewport.
 * Scrolling up: right column scrolls back to top before left
 *   media-extras leave the viewport.
 *
 * How it works:
 *   - The right column is made sticky + overflow:hidden via CSS.
 *   - A JS wrapper div inside it is translated upward as the page scrolls,
 *     revealing the rest of the form content (phase 1).
 *   - The left media-extras get margin-top = rightBudget so they only
 *     enter the viewport after phase 1 is exhausted (phase 2).
 *   - phase1 is driven by raw window.scrollY with no header-height offset,
 *     so --header-height changes never cause a snap or unwanted space.
 */
class PdpRail {
  /** @type {HTMLElement} */
  #section;
  /** @type {HTMLElement} */
  #detailsCol;
  /** @type {HTMLElement | null} */
  #mediaExtras;
  /** @type {HTMLElement} */
  #inner;
  /** @type {ResizeObserver} */
  #ro;
  /** @type {number} */
  #rightBudget = 0;
  /** @type {boolean} */
  #active = false;
  /** @type {number | null} */
  #scrollRafId = null;

  static #MQ = window.matchMedia('(min-width: 750px)');

  /** @param {HTMLElement} section */
  constructor(section) {
    this.#section = section;
    this.#detailsCol = section.querySelector('.product-information__details-column');
    this.#mediaExtras = section.querySelector('.product-information__media-extras');

    if (!this.#detailsCol) return;

    // Wrap all children of detailsCol into a single div we can translate.
    // Preserve flex layout so margin-top:auto on form-extras still works.
    this.#inner = document.createElement('div');
    this.#inner.className = 'pdp-rail__inner';
    while (this.#detailsCol.firstChild) {
      this.#inner.appendChild(this.#detailsCol.firstChild);
    }
    this.#detailsCol.appendChild(this.#inner);

    this.#ro = new ResizeObserver(this.#measure);
    this.#ro.observe(this.#inner);
    if (this.#mediaExtras) this.#ro.observe(this.#mediaExtras);

    PdpRail.#MQ.addEventListener('change', this.#onMqChange);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#measure, { passive: true });

    this.#measure();
  }

  #measure = () => {
    if (!PdpRail.#MQ.matches) {
      this.#deactivate();
      return;
    }

    // clientHeight = actual rendered column height (CSS: 100dvh - header-height).
    const vp = this.#detailsCol.clientHeight;
    // scrollHeight on the inner (flex-shrink:0) = true content height.
    const rh = this.#inner.scrollHeight;
    const newBudget = Math.max(0, rh - vp);

    // Guard against a ResizeObserver loop triggered by our own margin-top write.
    if (newBudget !== this.#rightBudget) {
      this.#rightBudget = newBudget;
      if (this.#mediaExtras) {
        this.#mediaExtras.style.marginTop = `${this.#rightBudget}px`;
      }
    }

    this.#active = true;
    this.#onScroll();
  };

  #onScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(this.#applyScroll);
  };

  #applyScroll = () => {
    this.#scrollRafId = null;
    if (!this.#active) return;
    // Drive phase1 from raw scrollY — no header-height offset at all.
    // The CSS positions both sticky columns correctly via --header-height;
    // the JS only needs to know how far the user has scrolled from the top.
    const phase1 = Math.min(Math.max(0, window.scrollY), this.#rightBudget);
    this.#inner.style.transform = phase1 > 0 ? `translate3d(0, -${phase1}px, 0)` : '';
  };

  #onMqChange = () => {
    if (PdpRail.#MQ.matches) {
      this.#measure();
    } else {
      this.#deactivate();
    }
  };

  #deactivate() {
    this.#active = false;
    this.#rightBudget = 0;
    if (this.#mediaExtras) this.#mediaExtras.style.marginTop = '';
    if (this.#inner) this.#inner.style.transform = '';
  }

  destroy() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#measure);
    PdpRail.#MQ.removeEventListener('change', this.#onMqChange);
    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
    this.#ro.disconnect();
    this.#deactivate();
  }
}

function initPdpRails() {
  document.querySelectorAll('.product-information.instinct-pdp').forEach((section) => {
    new PdpRail(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPdpRails);
} else {
  initPdpRails();
}
